import {
  ACTIONS,
  CHARTS,
  FIELD_TYPES,
  RESULT_TYPES,
} from "@/utils/calculatorSchema";

import {
  calculateLoanSummary,
  generateMonthlySchedule,
  getBalanceTransferInterestComparisonData,
  getBalanceTransferOutstandingComparisonData,
  getMonthlySavingsTrend,
  getEmiComparisonData,
  getTransferCostBreakdownData,
} from "@/utils/finance";

import {
  ArrowRightLeft,
  Wallet,
  Percent,
  CreditCard,
  PiggyBank,
  Clock3,
  ArrowLeftRight,
} from "lucide-react";

import DataTable from "@/components/calculator/DataTable";

import InsightCard from "@/components/calculator/InsightCard";

import { formatCurrency } from "@/utils/finance";

const balanceTransferCalculatorConfig = {
  id: "balance-transfer",

  shortTitle: "Balance Transfer",

  title: "Home Loan Balance Transfer Calculator",

  subtitle:
    "Compare your existing home loan with a new lender and estimate EMI savings, interest savings, transfer costs and break-even period.",

  icon: ArrowLeftRight,

  description:
    "Evaluate whether transferring your home loan to another lender can reduce your EMI, lower your interest outgo and improve your overall borrowing cost after considering processing fees and legal charges.",

  keywords: [
    "Balance Transfer Calculator",
    "Home Loan Balance Transfer",
    "Mortgage Balance Transfer",
    "Refinance Calculator",
    "Loan Transfer Calculator",
    "Interest Savings",
    "Home Loan Refinance",
  ],

  fields: [
    /* ------------------------------------------------------------ */
    /* Existing Loan                                                */
    /* ------------------------------------------------------------ */

    {
      key: "outstandingLoan",
      label: "Outstanding Loan Amount",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 5000000,
      min: 100000,
      max: 200000000,
      step: 100000,
    },

    {
      key: "currentRate",
      label: "Current Interest Rate",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 9,
      min: 1,
      max: 20,
      step: 0.05,
    },

    {
      key: "remainingTenure",
      label: "Remaining Tenure",
      suffix: "Years",
      type: FIELD_TYPES.NUMBER,
      defaultValue: 18,
      min: 1,
      max: 40,
      step: 1,
    },

    /* ------------------------------------------------------------ */
    /* New Loan                                                     */
    /* ------------------------------------------------------------ */

    {
      key: "newRate",
      label: "New Interest Rate",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 8.15,
      min: 1,
      max: 20,
      step: 0.05,
    },

    {
      key: "newTenure",
      label: "New Loan Tenure",
      suffix: "Years",
      type: FIELD_TYPES.NUMBER,
      defaultValue: 18,
      min: 1,
      max: 40,
      step: 1,
    },

    /* ------------------------------------------------------------ */
    /* Transfer Charges                                             */
    /* ------------------------------------------------------------ */

    {
      key: "processingFee",
      label: "Processing Fee",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 0.5,
      min: 0,
      max: 5,
      step: 0.05,
    },

    {
      key: "legalCharges",
      label: "Legal Charges",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 15000,
      min: 0,
      max: 500000,
      step: 1000,
    },

    {
      key: "otherCharges",
      label: "Other Charges",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 10000,
      min: 0,
      max: 500000,
      step: 1000,
    },

    /* ------------------------------------------------------------ */
    /* Optional                                                     */
    /* ------------------------------------------------------------ */

    {
      key: "topupLoan",
      label: "Top-up Loan",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 0,
      min: 0,
      max: 100000000,
      step: 50000,
    },
  ],

  summaryCards: [
    {
      title: "Current EMI",
      key: "currentEmi",
      type: RESULT_TYPES.CURRENCY,
      description: "Current monthly EMI.",
      icon: CreditCard,
    },

    {
      title: "New EMI",
      key: "newEmi",
      type: RESULT_TYPES.CURRENCY,
      description: "EMI after transferring the loan.",
      icon: ArrowRightLeft,
    },

    {
      title: "Monthly Savings",
      key: "monthlySavings",
      type: RESULT_TYPES.CURRENCY,
      description: "Reduction in monthly EMI.",
      icon: Wallet,
    },

    {
      title: "Interest Saved",
      key: "interestSaved",
      type: RESULT_TYPES.CURRENCY,
      description: "Total interest saved over the remaining tenure.",
      icon: Percent,
    },

    {
      title: "Break-even",
      key: "breakEvenMonths",
      displayKey: "breakEvenText",
      type: RESULT_TYPES.TEXT,
      description: "Time required to recover transfer costs.",
      icon: Clock3,
    },

    {
      title: "Net Savings",
      key: "netSavings",
      type: RESULT_TYPES.CURRENCY,
      description: "Savings after deducting transfer expenses.",
      icon: PiggyBank,
    },
  ],

  charts: [
    {
      id: "interest-comparison",

      title: "Interest Comparison",

      description:
        "Compare the remaining interest payable before and after transferring the loan.",

      type: CHARTS.PIE,

      resultsKey: "interestComparison",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,
    },

    {
      id: "outstanding-balance",

      title: "Outstanding Balance Comparison",

      description:
        "Compare outstanding balance under the existing and transferred loan.",

      type: CHARTS.LINE,

      resultsKey: "outstandingComparison",

      xAxis: {
        dataKey: "month",
      },

      lines: [
        {
          dataKey: "currentBalance",
          name: "Current Loan",
        },
        {
          dataKey: "transferredBalance",
          name: "Transferred Loan",
        },
      ],
    },

    {
      id: "monthly-savings",

      title: "Cumulative Interest Savings",

      description:
        "Shows how your cumulative interest savings grow after transferring the loan.",

      type: CHARTS.AREA,

      resultsKey: "monthlySavingsTrend",

      xAxis: {
        dataKey: "month",
      },

      areas: [
        {
          dataKey: "savings",
        },
      ],
    },

    {
      id: "emi-comparison",

      title: "Current EMI vs New EMI",

      type: CHARTS.PIE,

      resultsKey: "emiComparison",

      dataKey: "value",

      nameKey: "name",
    },

    {
      id: "transfer-cost",

      title: "Transfer Cost Breakdown",

      type: CHARTS.PIE,

      resultsKey: "transferCostBreakdown",

      dataKey: "value",

      nameKey: "name",
    },
  ],

  tables: [
    {
      title: "Current Loan Schedule",

      component: DataTable,

      dataKey: "currentSchedule",

      columns: [
        { key: "month", label: "Month", type: "number" },
        { key: "emi", label: "EMI", type: "currency" },
        { key: "principal", label: "Principal", type: "currency" },
        { key: "interest", label: "Interest", type: "currency" },
        { key: "balance", label: "Balance", type: "currency" },
      ],
    },

    {
      title: "Transferred Loan Schedule",

      component: DataTable,

      dataKey: "newSchedule",

      columns: [
        { key: "month", label: "Month", type: "number" },
        { key: "emi", label: "EMI", type: "currency" },
        { key: "principal", label: "Principal", type: "currency" },
        { key: "interest", label: "Interest", type: "currency" },
        { key: "balance", label: "Balance", type: "currency" },
      ],
    },

    {
      title: "Transfer Cost Breakdown",

      component: DataTable,

      dataKey: "costBreakdown",

      columns: [
        {
          key: "label",
          label: "Item",
          align: "left",
        },
        {
          key: "value",
          label: "Amount",
          type: RESULT_TYPES.CURRENCY,
        },
      ],
    },
  ],

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const {
      outstandingLoan,
      currentRate,
      remainingTenure,

      newRate,
      newTenure,

      processingFee,
      legalCharges,
      otherCharges,

      topupLoan,
    } = values;

    const transferAmount = outstandingLoan + topupLoan;

    /* ------------------------------------------------------------ */
    /* Existing Loan                                                */
    /* ------------------------------------------------------------ */

    const currentOptions = {
      principal: outstandingLoan,
      annualRate: currentRate,
      tenureYears: remainingTenure,
    };

    const currentLoan = calculateLoanSummary(currentOptions);

    const currentSchedule = generateMonthlySchedule(currentOptions);

    /* ------------------------------------------------------------ */
    /* New Loan                                                     */
    /* ------------------------------------------------------------ */

    const newOptions = {
      principal: transferAmount,
      annualRate: newRate,
      tenureYears: newTenure,
    };

    const newLoan = calculateLoanSummary(newOptions);

    const newSchedule = generateMonthlySchedule(newOptions);

    /* ------------------------------------------------------------ */
    /* Charges                                                      */
    /* ------------------------------------------------------------ */

    const processingFeeAmount = (transferAmount * processingFee) / 100;

    const transferCost = processingFeeAmount + legalCharges + otherCharges;

    /* ------------------------------------------------------------ */
    /* Savings                                                      */
    /* ------------------------------------------------------------ */

    const monthlySavings = currentLoan.emi - newLoan.emi;

    const interestSaved = currentLoan.totalInterest - newLoan.totalInterest;

    const netSavings = interestSaved - transferCost;

    const breakEvenMonths =
      monthlySavings > 0 ? Math.ceil(transferCost / monthlySavings) : null;

    let breakEvenText = "Not Applicable";

    if (breakEvenMonths) {
      const years = Math.floor(breakEvenMonths / 12);

      const months = breakEvenMonths % 12;

      if (years > 0 && months > 0) {
        breakEvenText = `${years} yr ${months} mo`;
      } else if (years > 0) {
        breakEvenText = `${years} yr`;
      } else {
        breakEvenText = `${months} mo`;
      }
    }

    /* ------------------------------------------------------------ */
    /* Cost Breakdown                                               */
    /* ------------------------------------------------------------ */

    const costBreakdown = [
      {
        label: "Processing Fee",
        value: processingFeeAmount,
      },
      {
        label: "Legal Charges",
        value: legalCharges,
      },
      {
        label: "Other Charges",
        value: otherCharges,
      },
      {
        label: "Total Transfer Cost",
        value: transferCost,
      },
    ];

    /* ------------------------------------------------------------ */
    /* Transfer Benefit Score                                       */
    /* ------------------------------------------------------------ */

    let score = 0;

    /* Interest Savings (40 points) */
    const interestSavingPercent =
      currentLoan.totalInterest > 0
        ? (interestSaved / currentLoan.totalInterest) * 100
        : 0;

    score += Math.min(40, interestSavingPercent * 2);

    /* Monthly EMI Savings (20 points) */
    const emiSavingPercent =
      currentLoan.emi > 0 ? (monthlySavings / currentLoan.emi) * 100 : 0;

    score += Math.min(20, emiSavingPercent * 2);

    /* Net Savings (20 points) */
    if (netSavings > 1000000) {
      score += 20;
    } else if (netSavings > 500000) {
      score += 16;
    } else if (netSavings > 250000) {
      score += 12;
    } else if (netSavings > 100000) {
      score += 8;
    } else if (netSavings > 0) {
      score += 4;
    }

    /* Break-even Period (20 points) */
    if (breakEvenMonths && breakEvenMonths <= 12) {
      score += 20;
    } else if (breakEvenMonths && breakEvenMonths <= 24) {
      score += 16;
    } else if (breakEvenMonths && breakEvenMonths <= 36) {
      score += 12;
    } else if (breakEvenMonths && breakEvenMonths <= 48) {
      score += 8;
    }

    score = Math.min(100, Math.round(score));

    let recommendation = {};

    if (score >= 80) {
      recommendation = {
        score,
        title: "Excellent Candidate",
        variant: "success",
        description:
          "A balance transfer is highly recommended. You'll recover the transfer costs quickly while achieving significant EMI and interest savings.",
      };
    } else if (score >= 60) {
      recommendation = {
        score,
        title: "Good Opportunity",
        variant: "success",
        description:
          "The balance transfer is financially beneficial and is likely to generate worthwhile long-term savings.",
      };
    } else if (score >= 40) {
      recommendation = {
        score,
        title: "Consider Carefully",
        variant: "warning",
        description:
          "The transfer provides moderate savings. Compare lender benefits, service quality and transfer charges before proceeding.",
      };
    } else {
      recommendation = {
        score,
        title: "Not Recommended",
        variant: "destructive",
        description:
          "The expected savings are relatively small compared to the transfer costs. Retaining your existing loan is likely to be the better option.",
      };
    }

    /* ------------------------------------------------------------ */
    /* Summary                                                      */
    /* ------------------------------------------------------------ */

    const summary = {
      currentEmi: currentLoan.emi,
      newEmi: newLoan.emi,

      monthlySavings,

      currentInterest: currentLoan.totalInterest,

      newInterest: newLoan.totalInterest,

      interestSaved,

      transferCost,

      processingFeeAmount,

      legalCharges,

      otherCharges,

      netSavings,

      breakEvenMonths,

      breakEvenText,

      currentSchedule,

      newSchedule,

      recommendation,

      benefitScore: score,
    };

    /* ------------------------------------------------------------ */
    /* Charts                                                       */
    /* ------------------------------------------------------------ */

    const charts = {
      interestComparison: getBalanceTransferInterestComparisonData(summary),

      outstandingComparison:
        getBalanceTransferOutstandingComparisonData(summary),

      monthlySavingsTrend: getMonthlySavingsTrend(summary),

      emiComparison: getEmiComparisonData(summary),

      transferCostBreakdown: getTransferCostBreakdownData(summary),
    };

    /* ------------------------------------------------------------ */
    /* Tables                                                       */
    /* ------------------------------------------------------------ */

    const tables = {
      currentSchedule,

      newSchedule,

      costBreakdown,
    };

    return {
      summary,
      charts,
      tables,
    };
  },

  exportExcel: ({ results }) => ({
    "Current Loan Schedule": results.tables.currentSchedule ?? [],

    "Transferred Loan Schedule": results.tables.newSchedule ?? [],

    "Transfer Cost Breakdown": results.tables.costBreakdown ?? [],
  }),

  renderInsights: ({ results }) => {
    const {
      currentEmi,
      newEmi,
      monthlySavings,

      interestSaved,
      transferCost,
      netSavings,

      breakEvenMonths,

      currentInterest,
      newInterest,
    } = results.summary;

    const { recommendation, benefitScore } = results.summary;

    return (
      <>
        <InsightCard
          title={recommendation.title}
          variant={recommendation.variant}
        >
          <div className="space-y-2">
            <div className="text-3xl font-bold">{benefitScore}/100</div>

            <p>{recommendation.description}</p>
          </div>
        </InsightCard>

        <InsightCard title="EMI Comparison" variant="info">
          <ul className="space-y-1 text-sm">
            <li>
              Current EMI: <strong>{formatCurrency(currentEmi)}</strong>
            </li>

            <li>
              New EMI: <strong>{formatCurrency(newEmi)}</strong>
            </li>

            <li>
              Monthly Savings: <strong>{formatCurrency(monthlySavings)}</strong>
            </li>
          </ul>
        </InsightCard>

        <InsightCard title="Interest Savings" variant="success">
          <ul className="space-y-1 text-sm">
            <li>
              Current Interest Payable:{" "}
              <strong>{formatCurrency(currentInterest)}</strong>
            </li>

            <li>
              New Interest Payable:{" "}
              <strong>{formatCurrency(newInterest)}</strong>
            </li>

            <li>
              Total Interest Saved:{" "}
              <strong>{formatCurrency(interestSaved)}</strong>
            </li>
          </ul>
        </InsightCard>

        <InsightCard title="Transfer Cost Analysis" variant="warning">
          <ul className="space-y-1 text-sm">
            <li>
              Total Transfer Cost:{" "}
              <strong>{formatCurrency(transferCost)}</strong>
            </li>

            <li>
              Net Savings After Costs:{" "}
              <strong>{formatCurrency(netSavings)}</strong>
            </li>

            <li>
              Break-even Period:{" "}
              <strong>
                {breakEvenMonths
                  ? `${breakEvenMonths} months`
                  : "Not Applicable"}
              </strong>
            </li>
          </ul>
        </InsightCard>

        <InsightCard title="Things to Consider" variant="info">
          <ul className="list-disc space-y-2 pl-5">
            <li>
              Verify that the new lender offers a lower effective interest rate
              and not just an introductory rate.
            </li>

            <li>
              Compare processing fees, legal expenses, valuation charges, stamp
              duty and documentation costs before making a decision.
            </li>

            <li>
              Check for foreclosure or prepayment charges with your current
              lender before initiating the transfer.
            </li>

            <li>
              If you're taking a top-up loan, ensure the additional borrowing
              aligns with your financial goals and repayment capacity.
            </li>

            <li>
              Review customer service, loan flexibility, prepayment options and
              digital banking experience in addition to the interest rate.
            </li>
          </ul>
        </InsightCard>
      </>
    );
  },
};

export default balanceTransferCalculatorConfig;
